import * as imgUtil from "@shared/imgUtil.js";
import { renderSettings, croppedDrawRect } from "@shared/RenderSettings.js";
import { Product } from "@shared/Product.js";
import { CYODesign } from "@shared/CYODesign.js";
import { Edge } from "@shared/BorderLoc.js";
import { arraysEqual } from "@shared/BorderLoc.js";
import { Rect } from "@shared/Rect.js";
import { ArrayUtil } from "@shared/utility.js";
import { RandomState } from "@shared/RandomState.js";
import { ContentRendererUtils } from "@shared/ContentRendererUtils.js";
import { dlog, dLogTime, dLogTimeEnd } from "@shared/utility.js";

export class ContentRendererPattern {
    
    static async drawPatternGridAlg(
        stickersCtx, 
        photoStickersCtx, 
        userImgVariant, 
        userImageFlatEdges, 
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign, 
        stickerImages, 
        placements,
        getPatternParam,
        popSticker,
        randomState = null
    ) {
        const product = device.productCategory;
    
        dlog(">> _drawPatternGridAlg");

        const safeAreaInsets = device.safeAreaInsets;
        const topMargin = ArrayUtil.top(safeAreaInsets) * canvasHeight;
        const rightMargin = ArrayUtil.right(safeAreaInsets) * canvasWidth;
        const bottomMargin = ArrayUtil.bottom(safeAreaInsets) * canvasHeight;
        const leftMargin = ArrayUtil.left(safeAreaInsets) * canvasWidth;
        const croppedCanvasWidth = canvasWidth - rightMargin - leftMargin;
        const croppedCanvasHeight = canvasHeight - topMargin - bottomMargin;

        const base_sticker_scale = renderSettings(product, "patternStickerScale");
        const rotationRandomness = renderSettings(product, "patternRotationRandomnessDeg");
        const translationalRandomness = renderSettings(product, "patternTranslationRandomness");
        const patternInsets = getPatternParam("patternInsets");
        const xsInset = patternInsets[0] * croppedCanvasWidth;
        const ysInset = patternInsets[1] * croppedCanvasHeight;
        
        const isPhotoPatternPlusStickerCorner = placements.photo.pattern && placements.stickers.corner;
        
        var actualUserImage;
        
        if (placements.photo.pattern) {
            // Round the bottom of the user image if necessary
            if (userImageFlatEdges.includes(Edge.bottom)) {
                actualUserImage = imgUtil.maskImageWithCircle(userImgVariant, userImgVariant.width / 2 * 1.2)
            } else {
                actualUserImage = userImgVariant;
            }

            // Performance: scale the user image now instead of doing it repeatedly below.
            const scale = base_sticker_scale * (device.stickerScaleFactor || 1.0) * canvasHeight / ((actualUserImage.width + actualUserImage.height / 2)) * 1.5;
            const newH = actualUserImage.height * scale;
            actualUserImage = imgUtil.resizeImageOrCanvasForHeight(actualUserImage, newH);
        }

        const patternCnt = getPatternParam("patternCnt");
        const nr = patternCnt[0];
        const nc = patternCnt[1];
        const interval = 3; // at what interval uploaded photo alternates with stickers
    
        var imgScale;
        const patternSpacing = getPatternParam("patternSpacing");
        const drRatio = patternSpacing[0];
        const dr = drRatio * canvasWidth;
        const dcRatio = patternSpacing[1];
        const dc = dcRatio * canvasWidth;
        const xs = leftMargin + xsInset;
        
        const gripPhotoPositions = RandomState.togglingRandomBool(`_drawPatternGridAlg-gripPhotoPositions`, randomState)
            ? [[0,0], [1,2], [2,0]]
            : [[0,1], [1,0], [2,1]]

        function chooseStickerOrPhoto(col, row) {
            if (!placements.stickers.pattern) {
                return CYODesign.StickerOrPhoto.photo;
            } else if (!placements.photo.pattern) {
                return CYODesign.StickerOrPhoto.sticker;
            } else {
                if (product == Product.grip) {
                    // Sadly we need to hardcode the grip positions since no version of the modulo algorithm looks right
                    // (We do randomize the placement with gripPhotoPositions though.)
                    const colRowA = [col, row];
                                        
                    if (gripPhotoPositions.some(a => arraysEqual(a, colRowA))) {
                        dlog(`photo: col: ${col}; row: ${row}; photoPositions: ${gripPhotoPositions}`)
                        return CYODesign.StickerOrPhoto.photo;
                    } else {
                        dlog(`sticker: col: ${col}; row: ${row}; photoPositions: ${gripPhotoPositions}`)
                        return CYODesign.StickerOrPhoto.sticker;
                    }
                } else {
                    return ((col * nr + row) % interval == 0)
                        ? CYODesign.StickerOrPhoto.photo
                        : CYODesign.StickerOrPhoto.sticker
                }
            }
        }
        
        for (let col = 0; col < nc; col++) {
            let ys = topMargin + ysInset;
            // Stagger columns
            const shouldStagger = product == Product.grip ? !(col % 2) : (col % 2);
            if (shouldStagger) {
                ys += dr / 2;
            }
    
            for (let row = 0; row < nr; row++) {
                
                // Avoid overlapping locations when doing this particular config
                if (isPhotoPatternPlusStickerCorner) {
                    var avoidLocs = [];
                    switch (product) {
                        case Product.case: avoidLocs = [[0, 4], [3, 0], [3, 4]]; break;
                        case Product.wallet: avoidLocs = [[0, 0], [4, 0]]; break;
                        // grip is OK-ish
                    }
                    if (avoidLocs.some(loc => loc[0] === col && loc[1] === row)) {
                        continue;
                    }
                }
                
                var img;
                const stickerOrPhoto = chooseStickerOrPhoto(col, row);
                if (stickerOrPhoto == CYODesign.StickerOrPhoto.sticker) {
                    [ , img] = popSticker();
                    var sticker_scale = base_sticker_scale;
                    const f = cyoDesign.stickerSet.patternStickerScale?.[product];
                    if (f) {
                        sticker_scale = f;
                    }
                    sticker_scale *= (device.stickerScaleFactor || 1.0);
                    imgScale = sticker_scale * canvasHeight / Math.max(img.height, img.width);
                } else {
                    img = actualUserImage;
                    imgScale = 1.0; // since it was resized above
                }
    
                const drawRect = new Rect();
                const randXTrans = imgUtil.linearRandom(-translationalRandomness, translationalRandomness, randomState) * croppedCanvasWidth;
                const randYTrans = imgUtil.linearRandom(-translationalRandomness, translationalRandomness, randomState) * croppedCanvasHeight;
                drawRect.x = xs + col * dc + randXTrans;
                drawRect.y = ys + row * dr + randYTrans;
                
                // #174 disableRotations should apply to user image too (border/pattern placements only)
                const rotationAngle = (cyoDesign.debugOptions.disableRotations || cyoDesign.stickerSet?.disableRotations)
                    ? 0
                    : imgUtil.linearRandom(-rotationRandomness, rotationRandomness, randomState) * Math.PI / 180;            
    
                var rotCanvas = imgUtil.rotatedCanvas(img, rotationAngle);
                rotCanvas = imgUtil.cropTransparentImage(rotCanvas);

                drawRect.width = rotCanvas.width * imgScale;
                drawRect.height = rotCanvas.height * imgScale;
                
                // Adjust position for non-square stickers to maintain visual centering
                // The grid spacing was designed for the base sticker scale, not custom scales
                const baseScale = renderSettings(product, "patternStickerScale");
                const assumedSquareStickerSize = baseScale * canvasHeight; // What the grid spacing expects
                const actualStickerWidth = drawRect.width;
                const actualStickerHeight = drawRect.height;
                
                const widthDiff = assumedSquareStickerSize - actualStickerWidth;
                const heightDiff = assumedSquareStickerSize - actualStickerHeight;
                
                
                drawRect.x += widthDiff / 2;
                drawRect.y += heightDiff / 2;
                
                
                
            
    
                const drawOutline = stickerOrPhoto == CYODesign.StickerOrPhoto.photo
                    ? cyoDesign.subjectOutlines
                    : stickerImages.length > 0 && cyoDesign.stickerOutlines;
    
                if (cyoDesign.debugOptions.drawBgs) { 
                    ContentRendererUtils.debugAddBg(rotCanvas, ContentRendererUtils.debugBgColor2);
                }

                const theCtx = stickerOrPhoto == CYODesign.StickerOrPhoto.sticker ? stickersCtx : photoStickersCtx;
                
                ContentRendererUtils.placeImage(theCtx, rotCanvas,
                    drawRect,
                    drawOutline
                );                        
            }
        }
    }

    // Uses random point sampling alg instead of a fixed grid.
    static async drawPatternRandomAlg(
        stickersCtx, 
        photoStickersCtx, 
        userImgVariant, 
        userImageFlatEdges, 
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign, 
        stickerImages, 
        placements,
        popSticker,
        randomState = null
    ) {
        const product = device.productCategory;
    
        dLogTime("_drawPatternRandomAlg");
        dlog(">> _drawPatternRandomAlg");
    
        const croppedCanvasRect = croppedDrawRect(device, canvasWidth, canvasHeight);

        const spacing = cyoDesign.stickerSet.patternSpacingRatio[product] * croppedCanvasRect.width;
        const base_sticker_scale = renderSettings(product, "patternStickerScale");
        const rotationRandomness = renderSettings(product, "patternRotationRandomnessDeg");

        var actualUserImage;
        
        if (placements.photo.pattern) {
            // Round the bottom of the user image if necessary
            if (userImageFlatEdges.includes(Edge.bottom)) {
                actualUserImage = imgUtil.maskImageWithCircle(userImgVariant, userImgVariant.width / 2 * 1.2)
            } else {
                actualUserImage = userImgVariant;
            }

            // Performance: scale the user image now instead of doing it repeatedly below.
            const scale = base_sticker_scale * (device.stickerScaleFactor || 1.0) * canvasHeight / ((actualUserImage.width + actualUserImage.height / 2)) * 1.5;
            const newH = actualUserImage.height * scale;
            actualUserImage = imgUtil.resizeImageOrCanvasForHeight(actualUserImage, newH);
        }

        const interval = 3; // at what interval uploaded photo alternates with stickers
        var placementIdx = 0;
        var imgScale;
        var placedDrawRects = [];
        var img;

        // local func returns StickerOrPhoto.photo or StickerOrPhoto.sticker
        function chooseStickerOrPhoto() {
            if (!placements.stickers.pattern) {
                return CYODesign.StickerOrPhoto.photo;
            } else if (!placements.photo.pattern) {
                return CYODesign.StickerOrPhoto.sticker;
            } else {
                return (placementIdx % interval == 0)
                    ? CYODesign.StickerOrPhoto.photo
                    : CYODesign.StickerOrPhoto.sticker
            }
        }
        
        let nextLoc;
        let rndStickerIdxs = ContentRendererUtils.getRandomOrderArray(stickerImages.length, randomState);
        let lastStickerIdx;
        let failureCnt = 0;
        let isTryingAfterFailure = false;
        
        do {
            const stickerOrPhoto = chooseStickerOrPhoto();
            
            if (stickerOrPhoto == CYODesign.StickerOrPhoto.sticker) {
                if (rndStickerIdxs.length==0) {
                    // repopulate images array to randomly sample
                    rndStickerIdxs = ContentRendererUtils.getRandomOrderArray(stickerImages.length, randomState);
                }

                lastStickerIdx = rndStickerIdxs.shift();
                img = stickerImages[lastStickerIdx];
                var sticker_scale = base_sticker_scale;
                const f = cyoDesign.stickerSet.patternStickerScaleForRandomAlg?.[product];
                if (f) {
                    sticker_scale = f;
                }
                sticker_scale *= (device.stickerScaleFactor || 1.0);
                imgScale = sticker_scale;
            } else {
                img = actualUserImage;
                imgScale = 1.0; // since it was resized above
            }
            
            // get the rotated canvas
            // #174 disableRotations should apply to user image too (border/pattern placements only)
            const rotationAngle = (cyoDesign.debugOptions.disableRotations || cyoDesign.stickerSet?.disableRotations)
                ? 0
                : imgUtil.linearRandom(-rotationRandomness, rotationRandomness, randomState) * Math.PI / 180;
            var rotCanvas = imgUtil.rotatedCanvas(img, rotationAngle);
            rotCanvas = imgUtil.cropTransparentImage(rotCanvas);
            
            // start our draw rect with rotated w/h
            const drawRect = new Rect();
            drawRect.width = rotCanvas.width * imgScale;
            drawRect.height = rotCanvas.height * imgScale;            

            // get a centered drawing point based on the size of the rotated canvas
            // (nb: nextLoc is an [x,y] array here; not a BorderLoc).
            nextLoc = ContentRendererUtils.getNextLoc(drawRect, croppedCanvasRect, placedDrawRects, spacing, randomState);
            
            if (nextLoc) {
                isTryingAfterFailure = false;
                failureCnt = 0;
                
                // set x/y adjusting for center
                drawRect.x = ArrayUtil.x(nextLoc) - drawRect.width/2;
                drawRect.y = ArrayUtil.y(nextLoc) - drawRect.height/2;
                
                placedDrawRects.push(drawRect);
                
                const drawOutline = stickerOrPhoto == CYODesign.StickerOrPhoto.photo
                    ? cyoDesign.subjectOutlines
                    : stickerImages.length > 0 && cyoDesign.stickerOutlines;
                
                if (cyoDesign.debugOptions.drawBgs) { 
                    ContentRendererUtils.debugAddBg(rotCanvas, ContentRendererUtils.debugBgColor2);
                }
                
                const theCtx = stickerOrPhoto == CYODesign.StickerOrPhoto.sticker ? stickersCtx : photoStickersCtx;
                
                ContentRendererUtils.placeImage(theCtx, rotCanvas,
                    drawRect,
                    drawOutline
                );                        
                
                placementIdx += 1;
            } else {
                if (failureCnt < 10) {
                    isTryingAfterFailure = true;
                    // Put lastStickerIdx back into rndStickerIdxs and try the next
                    rndStickerIdxs.splice(failureCnt, 0, lastStickerIdx);
                    
                    // Check there are enough items in rndStickerIdxs, repopulate if nec
                    if (rndStickerIdxs.length < failureCnt+1) {
                        rndStickerIdxs = ContentRendererUtils.getRandomOrderArray(stickerImages.length, randomState);
                    }
                    
                    lastStickerIdx = rndStickerIdxs[failureCnt+1];
                    failureCnt += 1;
                } else {
                    isTryingAfterFailure = false;
                }
            }
        } while (nextLoc || isTryingAfterFailure);
        
        dLogTimeEnd("_drawPatternRandomAlg");
    }
}